import React, { useMemo } from 'react';
import { StyleSheet, Text, View } from 'react-native';
import { FontFamily } from '../../../../constants/fonts';

import HeatMapSection from '../../components/HeatMapSection';
import AnalyticsChartCard from '../../components/AnalyticsChartCard';
import TicketsByPriorityCard from '../../components/TicketsByPriorityCard';

import type {
  HeatMapData,
  PrioritySegmentData,
  PriorityView,
  ResolutionPriorityData,
  TicketingVolumeTrendsPayload,
} from '../../components/types';
import VerticalBarChartCard from '../../components/VerticalBarChartCard';

interface SectionState<T> {
  data: T | null;
  loading: boolean;
  error: string | null;
  retry: () => void;
}

interface VolumeAndTrendsProps {
  weeklySection: SectionState<TicketingVolumeTrendsPayload['weeklyTicketVolume']>;
  ticketsByPrioritySection: SectionState<TicketingVolumeTrendsPayload['ticketsByPriority']>;
  resolutionByPrioritySection: SectionState<TicketingVolumeTrendsPayload['resolutionTimeByPriority']>;
  submissionHeatMapSection: SectionState<TicketingVolumeTrendsPayload['submissionHeatMap']>;
  resolutionHeatMapSection: SectionState<TicketingVolumeTrendsPayload['resolutionHeatMap']>;
  priorityView: PriorityView;
  onPriorityViewChange: (view: PriorityView) => void;
  hasActiveFilters?: boolean;
}

const SUBMISSION_HEATMAP_LEGEND: [string, string, string] = [
  '#F3F7FD',
  '#C6DCFB',
  '#005CBC',
];

const SUBMISSION_HEATMAP_CELLS: [string, string, string, string, string] = [
  '#F3F7FD',
  '#DAEEFA',
  '#C6DCFB',
  '#7FB1E8',
  '#005CBC',
];

const RESOLUTION_HEATMAP_LEGEND: [string, string, string] = [
  '#EEFEEC',
  '#CAF3C6',
  '#027C06',
];

const RESOLUTION_HEATMAP_CELLS: [string, string, string, string, string] = [
  '#F1FBEF',
  '#DDF9DA',
  '#CAF3C6',
  '#6FCF6A',
  '#027C06',
];

const VolumeAndTrends: React.FC<VolumeAndTrendsProps> = ({
  weeklySection,
  ticketsByPrioritySection,
  resolutionByPrioritySection,
  submissionHeatMapSection,
  resolutionHeatMapSection,
  priorityView,
  onPriorityViewChange,
  hasActiveFilters = false,
}) => {
  const weeklyVolume = useMemo(() => {
    const source = weeklySection.data;

    return {
      title: source?.title ?? 'Weekly Ticket Volume',
      points: (source?.series ?? []).map((item) => ({
        label: item.label,
        value: item.value,
      })),
    };
  }, [weeklySection.data]);

  const prioritySegments = useMemo<PrioritySegmentData[]>(() => {
    const priorityData = ticketsByPrioritySection.data;

    if (!priorityData) {
      return [];
    }

    const selectedData =
      priorityView === 'open'
        ? priorityData.open
        : priorityData.total;

    const displayOrder = ['critical', 'high', 'medium', 'low'];

    return selectedData.segments
      .map((segment) => ({
        name: segment.name,
        value: segment.value,
        percentage: segment.percentage,
        color: segment.color,
      }))
      .sort((a, b) => {
        const aIndex = displayOrder.indexOf(a.name.toLowerCase());
        const bIndex = displayOrder.indexOf(b.name.toLowerCase());
        return (
          (aIndex === -1 ? Number.MAX_SAFE_INTEGER : aIndex) -
          (bIndex === -1 ? Number.MAX_SAFE_INTEGER : bIndex)
        );
      });
  }, [ticketsByPrioritySection.data, priorityView]);

  const priorityTotalTickets = useMemo(() => {
    const priorityData = ticketsByPrioritySection.data;

    if (!priorityData) {
      return 0;
    }

    return priorityView === 'open'
      ? priorityData.open.totalTickets
      : priorityData.total.totalTickets;
  }, [ticketsByPrioritySection.data, priorityView]);

  const priorityLegendItems = useMemo(() => {
    const displayOrder = ['critical', 'high', 'medium', 'low'];

    return [...prioritySegments].sort((a, b) => {
      const aIndex = displayOrder.indexOf(a.name.toLowerCase());
      const bIndex = displayOrder.indexOf(b.name.toLowerCase());

      return (
        (aIndex === -1 ? Number.MAX_SAFE_INTEGER : aIndex) -
        (bIndex === -1 ? Number.MAX_SAFE_INTEGER : bIndex)
      );
    });
  }, [prioritySegments]);

  const resolutionByPriority = useMemo<ResolutionPriorityData[]>(() => {
    const displayOrder = ['critical', 'high', 'medium', 'low'];

    return [...(resolutionByPrioritySection.data ?? [])].sort((a, b) => {
      const aIndex = displayOrder.indexOf(a.name.toLowerCase());
      const bIndex = displayOrder.indexOf(b.name.toLowerCase());
      return (
        (aIndex === -1 ? Number.MAX_SAFE_INTEGER : aIndex) -
        (bIndex === -1 ? Number.MAX_SAFE_INTEGER : bIndex)
      );
    }).map((item) => ({
      name: item.name,
      value: item.value,
      unit: item.unit,
      color: item.color,
    }));
  }, [resolutionByPrioritySection.data]);

  const submissionHeatMap = useMemo<HeatMapData>(() => {
    return submissionHeatMapSection.data ?? {
      days: [],
      hours: [],
      matrix: [],
    };
  }, [submissionHeatMapSection.data]);

  const resolutionHeatMap = useMemo<HeatMapData>(() => {
    return resolutionHeatMapSection.data ?? {
      days: [],
      hours: [],
      matrix: [],
    };
  }, [resolutionHeatMapSection.data]);

  return (
    <View style={styles.content}>
      {/*
        No blocking "Loading..." text — each card below renders its own skeleton
        via its `loading` prop so the layout stays stable during API calls.
      */}
    <VerticalBarChartCard
      title={weeklyVolume.title || 'Weekly Ticket Volume (12 Week Trend)'}
      subtitle="Fixed rolling window of trailing 12 completed weeks."
      data={weeklyVolume.points.slice(-12).map(item => ({
        label: item.label,
        value: item.value,
        valueLabel: item.value.toLocaleString(),
      }))}
      metadataPosition="inside"
      loading={weeklySection.loading}
      skeletonBarCount={12}
      error={weeklySection.error}
      onRetry={weeklySection.retry}
      hasActiveFilters={hasActiveFilters}
    />

      <View style={styles.grid2}>
        <View style={styles.grid2ColItem}>
          <TicketsByPriorityCard
            segments={priorityLegendItems}
            totalTickets={priorityTotalTickets}
            view={priorityView}
            onViewChange={onPriorityViewChange}
            loading={ticketsByPrioritySection.loading}
            error={ticketsByPrioritySection.error}
            hasActiveFilters={hasActiveFilters}
          />
        </View>

        <View style={styles.grid2ColItem}>
          <AnalyticsChartCard
            title="Resolution Time by Priority"
            description="Average resolution time of Closed tickets"
            chartType="horizontal-bar"
            rows={resolutionByPriority.map(
              item => ({
                label: item.name,
                value: item.value,
                color: item.color,
                unit: item.unit,
              }),
            )}
            loading={resolutionByPrioritySection.loading}
            skeletonRowCount={4}
            error={resolutionByPrioritySection.error}
            hasActiveFilters={hasActiveFilters}
          />
        </View>
      </View>

      <View style={[styles.grid2, styles.gridTopSpacing]}>
        <HeatMapSection
          title="Submission Heat Map"
          note="Tickets submitted by day of week and hour. All times CST."
          data={submissionHeatMap}
          legendPalette={SUBMISSION_HEATMAP_LEGEND}
          cellPalette={SUBMISSION_HEATMAP_CELLS}
          loading={submissionHeatMapSection.loading}
          error={submissionHeatMapSection.error}
          hasActiveFilters={hasActiveFilters}
        />

        <HeatMapSection
          title="Resolution Heat Map"
          note="Tickets closed by day of week and hour. All times CST."
          data={resolutionHeatMap}
          legendPalette={RESOLUTION_HEATMAP_LEGEND}
          cellPalette={RESOLUTION_HEATMAP_CELLS}
          loading={resolutionHeatMapSection.loading}
          error={resolutionHeatMapSection.error}
          hasActiveFilters={hasActiveFilters}
        />
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  content: {
    padding: 2,
  },
  metaError: {
    fontSize: 12,
    fontFamily: FontFamily.medium,
    color: '#B42318',
    marginTop: 6,
    marginBottom: 8,
  },
  grid2: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 14,
    alignItems: 'stretch',
  },
  grid2ColItem: {
    flexGrow: 1,
    flexBasis: 0,
    minWidth: 380,
  },
  gridTopSpacing: {
    marginTop: 2,
  },
});

export default VolumeAndTrends;